// filepath: apps/web/src/features/cabinet/types.ts
//
// FR-UI-CABINET-1 — typed contracts for the tenant workspace shell.
//
// This module is the SINGLE SOURCE OF TRUTH for:
//   - the `NavManifest` contract (what the sidebar is allowed to
//     render — AC-2, INV-2 boundary);
//   - the admin-tile catalog (AC-7 — every tile deep-links an
//     EXISTING admin route; no admin page is modified by this FR).
//
// ────────────────────────────────────────────────────────────────────
// THE `/api/me` SOURCE-SWAP SEAM (AC-5 descope) ──────────────────────
// ────────────────────────────────────────────────────────────────────
// v1 derives `NavManifest` from the server-issued JWT
// (`realm_access.roles`) — no fetch, no `/api/me`. The
// `readableEntityTypes` field is the SEAM: today it is always `[]`
// (AC-5 — per-readable-entity-type Entities nav — is deferred to a
// follow-up `FR-UI-CORE-*` `/api/me` prerequisite, per the
// ImpactAnalyzer's Option-C decision).
//
// When `/api/me` ships, `useNavManifest` switches from deriving
// `adminAccess` / `readableEntityTypes` client-side to mapping
// `/api/me.resolved_permissions` → this same `NavManifest` shape.
// The contract below is STABLE; only the source changes. The
// follow-up FR populates `readableEntityTypes` in exactly one place
// (`useNavManifest.ts`), and the Entities section lights up with
// zero further shell changes.
//
// `/api/me` target shape (documented for the follow-up FR):
//   GET /api/me → 200 { profile, roles, resolved_permissions }
//   type MeResponse = {
//     profile: { sub: string; email: string };
//     roles: string[];                                    // realm_access.roles
//     resolved_permissions: {
//       admin_surface_access: boolean;                   // backs adminAccess
//       readable_entity_types: string[];                 // backs readableEntityTypes
//     };
//   };
//   cache key: ['api-me', tenantSlug, subject]  (FR-UI-CORE-2 AC-7)
// ────────────────────────────────────────────────────────────────────

/**
 * The server-derived navigation manifest. The sidebar renders
 * EXACTLY what this contract entitles — nothing client-guessed
 * (AC-2 / INV-2). The SPA never authorizes an action; it only
 * renders nav links. Every linked surface remains server-gated by
 * RBAC middleware regardless of what the manifest says.
 */
export interface NavManifest {
  /**
   * The always-present nav items. "My Cabinet" and "My Tasks" are
   * definitional — never permission-gated (every authenticated user
   * gets a landing and a task inbox entry point).
   */
  readonly alwaysPresent: readonly ["cabinet", "tasks"];

  /**
   * Whether the Processes surface nav item is shown.
   *
   * v1: `true` for every authenticated user. The failed-instance
   * dashboard (`/processes/instances/failed`) is an all-environments
   * read surface behind only `requireAuth` (no per-role gate exists
   * server-side today — FR-OBS-6); therefore every authenticated
   * user is entitled, and "the server signal entitles the user"
   * (AC-2) is satisfied by authentication itself. When `/api/me`
   * lands, this can be moved under `resolved_permissions` if a
   * per-role gate is introduced.
   */
  readonly processesAccess: boolean;

  /**
   * Whether the Admin section is rendered at all (AC-7).
   *
   * v1: derived from the JWT `realm_access.roles` claim
   * (`admin` role present). Fail-closed — absent/empty roles ⇒
   * `false` ⇒ Admin section is NOT rendered (not merely hidden).
   * The `admin` role is the recognized convention across the
   * codebase (`internal/entity/permissions.go` `isAdminBypass`,
   * tests seed `Roles: []string{"admin"}`). Admin pages remain
   * server-RBAC-gated regardless (INV-2).
   *
   * RISK (SecurityReviewer): `realm_access.roles` reachability in
   * `auth.user.profile` depends on the Keycloak realm's role mapper
   * targeting the userinfo/ID token. If the mapper is not
   * configured, `adminAccess` is `false` and the Admin nav is
   * hidden — fail-closed, no security impact (admins still reach
   * admin pages by URL; server RBAC enforces). The follow-up
   * `/api/me` FR retires this dependency.
   */
  readonly adminAccess: boolean;

  /**
   * The entity types the signed-in user may READ (AC-5).
   *
   * v1: ALWAYS `[]`. Per-entity-type READ permissions (FR-AUTH-5)
   * live in the tenant-schema `role_permissions` table and are
   * resolved server-side via `kernel.RoleStore.PermissionsForRole` —
   * NOT derivable from the JWT roles claim alone, and NOT surfaced
   * by any SPA-reachable signal today. The Entities section
   * therefore renders NOTHING in v1. Populating this from
   * `useEntityTypeList` is explicitly FORBIDDEN — it would show
   * links to entity types the user cannot read (AC-2/AC-5
   * violation). Deferred to the `/api/me` follow-up FR.
   */
  readonly readableEntityTypes: readonly string[];

  /**
   * Whether the Directory section is rendered at all
   * (FR-UI-DIR-1 AC-1..AC-10 — sidebar extension).
   *
   * v1: derived from the JWT `realm_access.roles` claim — the
   * `directory.access` role grants access. Fail-closed: absent /
   * empty / malformed roles ⇒ `false` ⇒ the Directory section is
   * NOT rendered (not merely hidden). Directory surfaces remain
   * server-RBAC-gated regardless of this signal (INV-2).
   *
   * The seam is documented in FR-UI-DIR-1 02-impact-analysis.md:
   * when `/api/me` ships, this will move under
   * `resolved_permissions.directory_access` and the JWT derivation
   * here will be retired.
   */
  readonly directoryAccess: boolean;
}

/**
 * One navigation tile in the Admin section grid (AC-7). Each tile
 * deep-links an EXISTING admin route — this FR does not modify any
 * individual admin page.
 */
export interface AdminTile {
  /** Stable id (for React keys + test hooks). */
  readonly id: string;
  /** The existing admin route this tile links into. */
  readonly route: string;
  /** i18n key for the visible label. */
  readonly labelKey: string;
  /** English fallback (used only when the key is absent). */
  readonly fallback: string;
}

/**
 * The admin-tile catalog (AC-7). Every entry deep-links an EXISTING
 * route already mounted by `AuthStateRouter` in `App.tsx`; no admin
 * page is created or modified by this FR.
 *
 * OMISSIONS:
 *   - "Field Visibility" (`/admin/roles/:roleName/field-visibility`)
 *     requires a `:roleName` path param and has no index default, so
 *     it cannot be a top-level tile. It remains reachable from within
 *     the Roles / Permissions Matrix pages (unchanged).
 *   - The form / process EDITORS (`/admin/forms/new`,
 *     `/admin/forms/:id/edit`, `/admin/processes/new`,
 *     `/admin/processes/:name/edit`) are full-screen focused
 *     workflows reached from the Forms / Processes list pages, not
 *     top-level tiles.
 *
 * The array is frozen so a future editor cannot accidentally reorder
 * or append without touching this audited source.
 */
export const ADMIN_TILES: readonly AdminTile[] = Object.freeze([
  {
    id: "users",
    route: "/admin/users",
    labelKey: "cabinet.admin.tile.users",
    fallback: "Roles",
  },
  {
    id: "permissions-matrix",
    route: "/admin/permissions/matrix",
    labelKey: "cabinet.admin.tile.permissions_matrix",
    fallback: "Permissions Matrix",
  },
  {
    id: "record-field-permissions",
    route: "/admin/permissions/record-field",
    labelKey: "cabinet.admin.tile.record_field_permissions",
    fallback: "Record & Field Permissions",
  },
  {
    id: "webhooks",
    route: "/admin/webhooks/endpoints",
    labelKey: "cabinet.admin.tile.webhooks",
    fallback: "Webhook Endpoints",
  },
  {
    id: "hmac-keys",
    route: "/admin/webhooks/keys",
    labelKey: "cabinet.admin.tile.hmac_keys",
    fallback: "HMAC Keys",
  },
  {
    id: "http-endpoints",
    route: "/admin/effects/http-endpoints",
    labelKey: "cabinet.admin.tile.http_endpoints",
    fallback: "HTTP Endpoints",
  },
  {
    id: "email-channels",
    route: "/admin/effects/email-channels",
    labelKey: "cabinet.admin.tile.email_channels",
    fallback: "Email Channels",
  },
  {
    id: "secrets",
    route: "/admin/secrets",
    labelKey: "cabinet.admin.tile.secrets",
    fallback: "Secrets",
  },
  {
    id: "templates",
    route: "/admin/templates/registry",
    labelKey: "cabinet.admin.tile.templates",
    fallback: "Templates",
  },
  {
    id: "template-updates",
    route: "/admin/templates/updates",
    labelKey: "cabinet.admin.tile.template_updates",
    fallback: "Template Updates",
  },
  {
    id: "tier",
    route: "/admin/tier",
    labelKey: "cabinet.admin.tile.tier",
    fallback: "Tier & Quota",
  },
  {
    id: "audit",
    route: "/admin/audit",
    labelKey: "cabinet.admin.tile.audit",
    fallback: "Audit Log",
  },
  {
    id: "forms",
    route: "/admin/forms",
    labelKey: "cabinet.admin.tile.forms",
    fallback: "Forms",
  },
  {
    id: "processes",
    route: "/admin/processes",
    labelKey: "cabinet.admin.tile.processes",
    fallback: "Processes",
  },
] as const);

/**
 * The cabinet landing route (the default authenticated fall-through).
 * Surfaced as a constant so the sidebar "My Cabinet" link and the
 * `AuthStateRouter` fall-through share one source of truth.
 */
export const CABINET_LANDING_PATH = "/" as const;

/** The "My Tasks" route (FR-UI-TASK-1). */
export const TASKS_PATH = "/tasks" as const;

/** The Processes failed-instance dashboard route (FR-UI-PROC-1). */
export const PROCESSES_PATH = "/processes/instances/failed" as const;

/** The Admin section index route (AC-7 tile grid). */
export const ADMIN_INDEX_PATH = "/admin" as const;
